import React from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, matchPath } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Layout } from 'antd';
import classNames from 'classnames';
import ReconciliationDashboard from '~pages/reconciliation';
import OrdersDashboard from '~pages/longtail-bookings';
import PaymentDashboard from '~pages/payment';
import PmsDashboard from '~pages/pms-dashboard';
import Dashboard from '~pages/dashboard';
import Withdrawal from '~pages/withdrawal';
import Students from '~pages/students';
import { LogoSmall as LogoSmallIcon, LogoMain as LogoMainIcon } from 'common/components/svgs';
import Header from '~components/pms-header';
import Footer from '~components/footer';
import MessageCenterIcon from '~components/message-center-icon';
import * as actions from '~actions/auth';
import { fireCustomEvent } from '~helpers/custom-events';
import { importModal } from '~helpers/import-modal';
import * as accountActions from '~actions/pms-actions/account';
import MenuList from '~pages/menu';

const mapStateToProps = state => ({
  status: state.auth.getIn(['base', 'status']),
  companyID: state.auth.getIn(['base', 'companyID']),
  companyName: state.auth.getIn(['base', 'companyName']),
  token: state.auth.getIn(['base', 'token']),
  landlordUser: state.pmsReducer.dashboard.account.toJS().account,
  hasHerculesProperties: state.auth.getIn(['properties', 'hasHerculesProperties']),
  hasInstantBookProperties: state.auth.getIn(['properties', 'hasInstantBookProperties']),
  hasInstantBookings: state.auth.getIn(['properties', 'hasInstantBookings']),
});

const mapDispatchToProps = dispatch => ({
  getLandlordType: () => {
    dispatch(actions.getLandlordType());
  },
  logoutUser: () => {
    dispatch(actions.logout());
  },
  getLandlordUser: () => {
    dispatch(accountActions.getLandlordUser(() => {}));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
export default class RouterComponents extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      collapsed: false,
      automaticCloseMenu: true,
      accountModalImported: false,
      accountModalLoaded: false,
    };

    this.isLogged = this.props.token;
  }

  componentDidMount() {
    if (this.isLogged) {
      this.props.getLandlordType();
      this.props.getLandlordUser();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.isLogged && !prevProps.token) {
      this.props.getLandlordType();
      this.props.getLandlordUser();
    }

    if (
      !this.state.accountModalImported &&
      this.props.landlordUser?.liveChatEnabled &&
      ['hasHerculesProperties', 'hasInstantBookProperties', 'hasInstantBookings'].some(
        key => this.props[key],
      )
    ) {
      const importModalCallBack = () => {
        this.setState({
          accountModalLoaded: true,
        });
      };

      if (window.hurricane?.staticHash) {
        importModal(importModalCallBack);
      } else {
        document.addEventListener('staticHashInitialised', () => {
          importModal(importModalCallBack);
        });
      }

      this.setState({
        accountModalImported: true,
      });
    }
  }

  closeMenu = () => {
    if (this.state.automaticCloseMenu) {
      this.setState({
        collapsed: true,
        automaticCloseMenu: false,
      });
    }
  };

  toggleCollapseSidebar = () => {
    this.customSidebar = true;
    this.setState({
      collapsed: !this.state.collapsed,
    });

    setTimeout(() => {
      fireCustomEvent('toggleCollapseSidebarTriggers');
    }, 200);
  };

  render() {
    const { accountModalLoaded } = this.state;
    const { Sider, Content } = Layout;

    return (
      <Route
        render={routeProps => (
          <Layout>
            <Sider
              collapsible={false}
              collapsed={this.state.collapsed}
              width={this.state.collapsed ? '64' : '200'}
              trigger="null"
              className={classNames('sidebar', {
                'sidebar--collapsed': this.state.collapsed,
              })}
            >
              <div className="sidebar__logo-container">
                <If condition={this.state.collapsed}>
                  <LogoSmallIcon className="sidebar__logo-small" />
                </If>
                <If condition={!this.state.collapsed}>
                  <LogoMainIcon className="sidebar__logo" />
                </If>
              </div>
              <MenuList collapsed={this.state.collapsed} routeProps={routeProps} />
            </Sider>
            <Layout style={{ minWidth: '960px' }}>
              <Header
                t={this.props.t}
                landlordUser={this.props.landlordUser}
                collapsed={this.state.collapsed}
                toggleCollapseSidebar={this.toggleCollapseSidebar}
                location={routeProps.location}
                history={routeProps.history}
                currentRoleSlug={'landlord'}
                logoutUser={this.props.logoutUser}
              />
              <Content>
                <Switch>
                  <For
                    each="path"
                    of={[
                      '/properties',
                      '/property',
                      '/account',
                      '/account/listing-plan',
                      '/account/profile',
                      '/account/security',
                    ]}
                  >
                    <Route
                      key={path}
                      path={path}
                      render={props => (
                        <PmsDashboard
                          {...props}
                          companyID={this.props.companyID}
                          companyName={this.props.companyName}
                          hasHerculesProperties={this.props.hasHerculesProperties}
                        />
                      )}
                    />
                  </For>
                  {/* TODO: to change url */}
                  <Route
                    path="/board"
                    render={() => <Dashboard landlord={this.props.landlordUser} />}
                  />
                  <Route path="/reconciliation" component={ReconciliationDashboard} />
                  <Route
                    path="/payment"
                    render={props => (
                      <PaymentDashboard {...props} hasKeyProperty={this.props.hasKeyProperty} />
                    )}
                  />
                  <Route path="/booking" component={OrdersDashboard} />
                  <Route path="/students" component={Students} />
                  <Route path="/withdrawal" component={Withdrawal} />
                </Switch>
              </Content>

              <If condition={accountModalLoaded}>
                <MessageCenterIcon />
              </If>

              <If
                condition={
                  !matchPath(routeProps.location?.pathname, '/profile') &&
                  !matchPath(routeProps.location?.pathname, '/booking/list')
                }
              >
                <Footer />
              </If>
            </Layout>
          </Layout>
        )}
      />
    );
  }
}

RouterComponents.propTypes = {
  companyID: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  token: PropTypes.string,
  getLandlordType: PropTypes.func.isRequired,
  t: PropTypes.func,
  landlordUser: PropTypes.object,
  getLandlordUser: PropTypes.func,
  logoutUser: PropTypes.func,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  status: PropTypes.string,
  hasHerculesProperties: PropTypes.bool,
};

RouterComponents.defaultProps = {
  companyID: '',
  companyName: '',
  token: '',
  landlordUser: null,
  getLandlordType: () => {},
  getLandlordUser: () => {},
  logoutUser: () => {},
  t: () => {},
  location: {
    search: '',
  },
  hasHerculesProperties: false,
};
